import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { prepareFiles, stringToPayloadFormat } from 'web-library';
import { MutationStatus, UseMutateFunction } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { useHistory } from 'react-router-dom';
import { RequestFileResponse } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { AxiosError } from 'axios';
import { UseFormReturn } from 'react-hook-form';
import { usePutRequestProfileAmendment } from '../../../api-refactored/profile-amendment-requests/usePutRequestProfileAmendment';
import { IdType } from '../../../models';
import { IExamMasterCodeItem } from '../../../api/user/model';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import {
  GeneratePresignedUrlForUploadingAmendmentRequestFiles,
  usePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles,
} from '../../../api-refactored/profile-amendment-requests/usePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles';
import { useWOGAA } from '../../../hooks/wogaa';
import { useGetCandidateProfileDetails } from '../../../api-refactored/user/useGetCandidateProfileDetails';

export const useSetCandidatePrStatusCitizenshipType = ({
  setPrStatus,
  setCitizenshipType,
  formHook,
}: {
  setPrStatus: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
  setCitizenshipType: React.Dispatch<React.SetStateAction<IExamMasterCodeItem | undefined | {}>>;
  formHook: UseFormReturn;
}): {
  handleIdTypeOnChange: (_e: unknown, idType: any) => void;
  handlePrStatusOnChange: (_e: unknown, prValue: any) => void;
} => {
  const { setValue, trigger } = formHook;

  const { data: iExamsData } = useGetIExamMasterCode();

  const selectedIdType = formHook?.watch('IdType');

  const isFin =
    formHook?.watch('NricFinPassportNo')?.[0] === 'F' ||
    formHook?.watch('NricFinPassportNo')?.[0] === 'G' ||
    formHook?.watch('NricFinPassportNo')?.[0] === 'M';

  const setPrStatusCitizenTypeToForeigner = useCallback(() => {
    const iExamsDataISCitizenshipCodeValue = iExamsData?.citizenship.find(
      (data) => data.codeValue === 'IS'
    );

    const prStatusForForeigner = {
      ...iExamsDataISCitizenshipCodeValue,
      codeValueDescription: 'FOREIGNER',
    };

    const iExamsDataAPrStatusCodeValue = iExamsData?.prStatus.find(
      (data) => data.codeValue === 'A'
    );
    formHook?.setValue('prStatus', iExamsDataAPrStatusCodeValue);
    formHook?.setValue('citizenship', iExamsDataISCitizenshipCodeValue);
    formHook?.trigger('prStatus');
    setPrStatus(prStatusForForeigner);

    setCitizenshipType(iExamsDataISCitizenshipCodeValue);
  }, [formHook, iExamsData?.citizenship, iExamsData?.prStatus, setCitizenshipType, setPrStatus]);

  const resetPrStatusCitizenType = useCallback(() => {
    formHook?.setValue('prStatus', null);
    formHook?.setValue('citizenship', {});
    setPrStatus({});
    setCitizenshipType({});
  }, [formHook, setCitizenshipType, setPrStatus]);

  const handleIdTypeOnChange = useCallback(
    (_e: any, idType: any) => {
      setValue('IdType', idType?.key);
      trigger('IdType');
      if (idType && idType.key === 'P') {
        setPrStatusCitizenTypeToForeigner();
      } else {
        resetPrStatusCitizenType();
      }
    },
    [resetPrStatusCitizenType, setPrStatusCitizenTypeToForeigner, setValue, trigger]
  );

  const handlePrStatusOnChange = useCallback(
    (_e: any, prValue: any) => {
      setValue('prStatus', prValue);
      trigger('prStatus');
      setPrStatus(prValue);
      if (prValue?.codeValue === 'S') {
        const iExamsDataSCCitizenshipCodeValue = iExamsData?.citizenship.find(
          (data) => data.codeValue === 'SC'
        );
        setValue('citizenship', iExamsDataSCCitizenshipCodeValue);
        setCitizenshipType(iExamsDataSCCitizenshipCodeValue);
      }
      if (prValue?.codeValue === 'P') {
        const iExamsDataPrCitizenshipCodeValue = iExamsData?.citizenship.find(
          (data) => data.codeValue === 'PR'
        );
        setValue('citizenship', iExamsDataPrCitizenshipCodeValue);
        setCitizenshipType(iExamsDataPrCitizenshipCodeValue);
      }
    },
    [iExamsData?.citizenship, setCitizenshipType, setPrStatus, setValue, trigger]
  );

  useEffect(() => {
    if (isFin) {
      setPrStatusCitizenTypeToForeigner();
    } else if (!formHook?.watch('NricFinPassportNo')?.length && selectedIdType !== 'P') {
      resetPrStatusCitizenType();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFin]);

  return {
    handleIdTypeOnChange,
    handlePrStatusOnChange,
  };
};

interface useHandleSubmitForApprovalWithFileUploadProps {
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setUploadFileErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  formHook?: UseFormReturn;
}

export const useHandleSubmitForApprovalWithFileUpload = ({
  setApiButtonStatus,
  setErrorMessage,
  setUploadFileErrorMessage,
  files,
  setFiles,
  formHook,
}: useHandleSubmitForApprovalWithFileUploadProps): {
  handleSubmitForApproval: () => void;
  mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles: UseMutateFunction<
    RequestFileResponse[],
    AxiosError,
    GeneratePresignedUrlForUploadingAmendmentRequestFiles
  >;
} => {
  const history = useHistory();

  const { getValues, watch } = formHook ?? {};

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const { data: iExamsData } = useGetIExamMasterCode();
  const { data: candidateProfileData } = useGetCandidateProfileDetails();
// wogaa transaction end for candidate profile amendment request submission
  const { transactionEnd } = useWOGAA('myexamsseab-6471');

  const { mutate: mutatePutRequestProfileAmendment } = usePutRequestProfileAmendment({
    onSuccess: () => {
      // Pass user uuid as agencyTxnId for WOGAA sentiments tracking
      transactionEnd(candidateProfileData?.uuid);
      setApiButtonStatus('success');
      history.push('.');
    },
    onError: (err) => {
      setApiButtonStatus('error');
      setErrorMessage(err?.response?.data?.message!);
      setShouldSubmit(false);
    },
  });

  const isNoPersonalParticularsFieldEntered =
    watch && !watch('name') && !watch('dateOfBirth') && !watch('NricFinPassportNo');

  const {
    mutate: mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles,
  } = usePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value: any, index: any) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
    },
    onError: (err) => {
      setApiButtonStatus('error');
      setUploadFileErrorMessage(err?.response?.data?.message!);
      setShouldSubmit(false);
    },
  });

  const handleSubmitForApproval = useCallback(() => {
    const { filenames } = prepareFiles(files, setFiles);
    setShouldSubmit(true);
    setApiButtonStatus('loading');

    if (filenames.length) {
      mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles({
        filenames,
        uuid: null,
      });
    }
  }, [
    files,
    mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles,
    setApiButtonStatus,
    setFiles,
    setShouldSubmit,
  ]);

  const selectedIdType = getValues?.('IdType');

  const getUserIdPayload = useMemo(() => {
    if (selectedIdType === 'I') {
      return IdType.NRIC_OR_FIN;
    }
    if (selectedIdType === 'P') {
      return IdType.PASSPORT;
    }
    return null;
  }, [selectedIdType]);

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.some((f) => f.hasError);

    const fileKeys: string[] = [];
    files.forEach((f) => {
      if (f.key) {
        fileKeys.push(f.key);
      }
    });

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }

    if (isAllFilesUploaded && shouldSubmit && getValues) {
      const iExamsDataAPrStatusCodeValue = iExamsData?.prStatus.find(
        (data) => data.codeValue === 'A'
      );

      const iExamsDataISCitizenshipCodeValue = iExamsData?.citizenship.find(
        (data) => data.codeValue === 'IS'
      );

      const prStatusValue =
        selectedIdType === 'P'
          ? iExamsDataAPrStatusCodeValue?.codeValueDescription
          : (getValues('prStatus')?.codeValueDescription as string);

      const citizenshipValue =
        selectedIdType === 'P'
          ? iExamsDataISCitizenshipCodeValue?.codeValueDescription
          : (getValues('citizenship')?.codeValueDescription as string);
      mutatePutRequestProfileAmendment({
        candidateProfileAmendmentModel: {
          name: (getValues('name') as string).trim() || null,
          userIdType: getUserIdPayload,
          identity: (getValues('NricFinPassportNo') as string) || null,
          dateOfBirth: getValues('dateOfBirth')
            ? stringToPayloadFormat(getValues('dateOfBirth'))
            : null,
          prStatus: prStatusValue || null,
          citizenship: citizenshipValue || null,
        },
        requestDetail: {
          requestorRemarks: getValues('remarks')?.trim() as string,
          fileKeys,
        },
      });
    }
  }, [
    files,
    getUserIdPayload,
    getValues,
    iExamsData?.citizenship,
    iExamsData?.prStatus,
    mutatePutRequestProfileAmendment,
    selectedIdType,
    shouldSubmit,
  ]);

  useEffect(() => {
    if (!isNoPersonalParticularsFieldEntered) {
      setErrorMessage('');
    }
    if (files.length) {
      setUploadFileErrorMessage('');
    }
  }, [
    files.length,
    isNoPersonalParticularsFieldEntered,
    setErrorMessage,
    setUploadFileErrorMessage,
  ]);

  return {
    handleSubmitForApproval,
    mutatePostGeneratePresignedUrlForUploadingProfileAmendmentRequestFiles,
  };
};
